import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "store";
import { Game } from "types";

// Base selector for all games
export const selectAllGames = (state: RootState) => state.games.allGames;
export const selectGamesLoading = (state: RootState) => state.games.gamesLoading;
export const selectGamesError = (state: RootState) => state.games.gamesError;

// Memoized selectors for specific game types
export const selectPublicCashGames = createSelector(
  [selectAllGames],
  (allGames): Record<string, Game> => {
    if (!allGames) return {};
    const result: Record<string, Game> = {};
    Object.entries(allGames).forEach(([id, game]) => {
      if (game.access === "Public" && game.type === "Cash") {
        result[id] = game;
      }
    });
    return result;
  }
);

export const selectPublicTournamentGames = createSelector(
  [selectAllGames],
  (allGames): Record<string, Game> => {
    if (!allGames) return {};
    const result: Record<string, Game> = {};
    Object.entries(allGames).forEach(([id, game]) => {
      if (game.access === "Public" && game.type === "Tournament") {
        result[id] = game;
      }
    });
    return result;
  }
);

export const selectPrivateCashGames = createSelector(
  [selectAllGames],
  (allGames): Record<string, Game> => {
    if (!allGames) return {};
    const result: Record<string, Game> = {};
    Object.entries(allGames).forEach(([id, game]) => {
      if (game.access === "Private" && game.type === "Cash") {
        result[id] = game;
      }
    });
    return result;
  }
);

export const selectPrivateTournamentGames = createSelector(
  [selectAllGames],
  (allGames): Record<string, Game> => {
    if (!allGames) return {};
    const result: Record<string, Game> = {};
    Object.entries(allGames).forEach(([id, game]) => {
      if (game.access === "Private" && game.type === "Tournament") {
        result[id] = game;
      }
    });
    return result;
  }
);

export const selectPublicGames = createSelector(
  [selectAllGames],
  (allGames): Record<string, Game> => {
    if (!allGames) return {};
    const result: Record<string, Game> = {};
    Object.entries(allGames).forEach(([id, game]) => {
      if (game.access === "Public") {
        result[id] = game;
      }
    });
    return result;
  }
);

export const selectPrivateGames = createSelector(
  [selectAllGames],
  (allGames): Record<string, Game> => {
    if (!allGames) return {};
    const result: Record<string, Game> = {};
    Object.entries(allGames).forEach(([id, game]) => {
      if (game.access === "Private") {
        result[id] = game;
      }
    });
    return result;
  }
);

export const selectCashGames = createSelector(
  [selectAllGames],
  (allGames): Record<string, Game> => {
    if (!allGames) return {};
    const result: Record<string, Game> = {};
    Object.entries(allGames).forEach(([id, game]) => {
      if (game.type === "Cash") {
        result[id] = game;
      }
    });
    return result;
  }
);

export const selectTournamentGames = createSelector(
  [selectAllGames],
  (allGames): Record<string, Game> => {
    if (!allGames) return {};
    const result: Record<string, Game> = {};
    Object.entries(allGames).forEach(([id, game]) => {
      if (game.type === "Tournament") {
        result[id] = game;
      }
    });
    return result;
  }
);

// Utility selector for games count by type
export const selectGamesCount = createSelector(
  [selectAllGames],
  (allGames) => {
    if (!allGames) {
      return {
        total: 0,
        publicCash: 0,
        publicTournament: 0,
        privateCash: 0,
        privateTournament: 0,
      };
    }

    const counts = {
      total: 0,
      publicCash: 0,
      publicTournament: 0,
      privateCash: 0,
      privateTournament: 0,
    };

    Object.values(allGames).forEach((game) => {
      counts.total++;
      if (game.access === "Public" && game.type === "Cash") {
        counts.publicCash++;
      } else if (game.access === "Public" && game.type === "Tournament") {
        counts.publicTournament++;
      } else if (game.access === "Private" && game.type === "Cash") {
        counts.privateCash++;
      } else if (game.access === "Private" && game.type === "Tournament") {
        counts.privateTournament++;
      }
    });

    return counts;
  }
);

// Backward compatibility selectors that fallback to legacy state if unified data not available
export const selectPublicCashGamesCompat = createSelector(
  [selectPublicCashGames, (state: RootState) => state.games.publicCashList],
  (unifiedGames, legacyGames) => {
    return Object.keys(unifiedGames).length > 0 ? unifiedGames : legacyGames;
  }
);

export const selectPublicTournamentGamesCompat = createSelector(
  [selectPublicTournamentGames, (state: RootState) => state.games.publicTourneyList],
  (unifiedGames, legacyGames) => {
    return Object.keys(unifiedGames).length > 0 ? unifiedGames : legacyGames;
  }
);

export const selectPrivateCashGamesCompat = createSelector(
  [selectPrivateCashGames, (state: RootState) => state.games.privateCashList],
  (unifiedGames, legacyGames) => {
    return Object.keys(unifiedGames).length > 0 ? unifiedGames : legacyGames;
  }
);

export const selectPrivateTournamentGamesCompat = createSelector(
  [selectPrivateTournamentGames, (state: RootState) => state.games.privateTourneyList],
  (unifiedGames, legacyGames) => {
    return Object.keys(unifiedGames).length > 0 ? unifiedGames : legacyGames;
  }
);